import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ComplaintDetailModalComponent } from './complaint-detail-modal.component';
import { EntityService } from '../../services/entity.service';
import { NotificationService } from '../../services/notification.service';
import { getCurrentUserId, getCurrentUser } from '../../helpers/utils';

interface Complaint {
  id: number;
  type: any;
  status: string;
  payment_reference: string;
  description: string;
  reference: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  user: {
    id: number;
    name: string;
    nom: string;
    prenom: string;
    email: string;
    telephone: string;
    profile_picture: string;
    rang: string;
    _hideImage?: boolean;
  };
  against_type?: string;
  against_id?: string;
  against?: any;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
}

@Component({
  selector: 'app-complaints',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './complaints.component.html',
  styleUrls: ['./complaints.component.scss']
})
export class ComplaintsComponent {
  complaints: Complaint[] = [];
  fanComplaints: Complaint[] = [];
  starComplaints: Complaint[] = [];
  platformComplaints: Complaint[] = [];
  isLoading = false;
  activeTab: 'fans' | 'stars' | 'platforms' = 'stars';

  // Helper method for type-safe tab comparison
  isTab(tab: 'fans' | 'stars' | 'platforms'): boolean {
    return this.activeTab === tab;
  }

  // Fallback method to ensure cards are displayed
  shouldShowStarsTab(): boolean {
    return this.isTab('stars');
  }

  shouldShowFansTab(): boolean {
    return this.isTab('fans');
  }

  shouldShowPlatformsTab(): boolean {
    return this.isTab('platforms');
  }

  // Pagination
  pagination: PaginationInfo = {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0
  };

  // Pagination par type
  starPagination: PaginationInfo = {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0
  };

  fanPagination: PaginationInfo = {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0
  };

  platformPagination: PaginationInfo = {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0
  };

  currentUserId: any;
  currentUser: any;

  constructor(
    private entityService: EntityService,
    private notificationService: NotificationService,
    private modalService: NgbModal
  ) {
    this.currentUserId = getCurrentUserId();
    this.currentUser = getCurrentUser();
    this.loadComplaints();
  }

  isCurrentUser(id: any): boolean {
    return id == this.currentUserId;
  }

  ngOnInit() {
    this.loadComplaints();
  }

  loadComplaints(page: number = 1) {
    this.isLoading = true;
    const url = `/user-complaints?page=${page}&per_page=${this.pagination.per_page}`;
    this.entityService.getDatas2(url).subscribe({
      next: (response: any) => {
        console.log(response);
        this.complaints = response.data || [];
        this.pagination = response.pagination || this.pagination;
        this.filterComplaintsByUserType();
        this.isLoading = false;
      },
      error: (error) => {
        this.notificationService.error('Erreur lors du chargement des plaintes');
        this.complaints = [];
        this.filterComplaintsByUserType();
        this.isLoading = false;
      }
    });
  }

  filterComplaintsByUserType() {
    if (!this.complaints || !Array.isArray(this.complaints)) {
      this.fanComplaints = [];
      this.starComplaints = [];
      this.platformComplaints = [];
      this.updatePaginations();
      return;
    }

    this.starComplaints = this.complaints.filter(complaint =>
      complaint.against_type === 'celeb' || (complaint as any).against_type === 'star'
    );
    this.fanComplaints = this.complaints.filter(complaint =>
      complaint.against_type === 'fan'
    );
    this.platformComplaints = this.complaints.filter(complaint =>
      complaint.against_type === 'yokastar' || !complaint.against_type
    );

    this.updatePaginations();
  }

  updatePaginations() {
    // Mettre à jour les informations de pagination pour chaque type
    this.updatePaginationInfo(this.starComplaints, this.starPagination);
    this.updatePaginationInfo(this.fanComplaints, this.fanPagination);
    this.updatePaginationInfo(this.platformComplaints, this.platformPagination);
  }

  updatePaginationInfo(complaints: Complaint[], paginationInfo: PaginationInfo) {
    const total = complaints.length;
    const perPage = paginationInfo.per_page;
    const currentPage = paginationInfo.current_page;
    const lastPage = Math.ceil(total / perPage) || 1;
    const from = total > 0 ? ((currentPage - 1) * perPage) + 1 : 0;
    const to = Math.min(currentPage * perPage, total);

    paginationInfo.total = total;
    paginationInfo.last_page = lastPage;
    paginationInfo.from = from;
    paginationInfo.to = to;
  }

  getCurrentPagination(): PaginationInfo {
    if (this.activeTab === 'stars') {
      return this.starPagination;
    } else if (this.activeTab === 'fans') {
      return this.fanPagination;
    } else if (this.activeTab === 'platforms') {
      return this.platformPagination;
    } else {
      return this.pagination;
    }
  }

  setActiveTab(tab: 'fans' | 'stars' | 'platforms') {
    this.activeTab = tab;
  }

  getTypeLabel(type: any): string {
    if (!type) return '-';
    // Si c'est un objet type avec un nom, on l'utilise directement
    if (typeof type === 'object' && type.name) {
      return type.name;
    }
    // Fallback pour les anciennes chaînes ou codes
    const types: { [key: string]: string } = {
      'payment_failed': 'Paiement échoué',
      'payment_pending': 'Paiement en attente',
      'service_quality': 'Qualité du service',
      'platform': 'Plateforme',
      'other': 'Autre'
    };
    return types[type as string] || type as string;
  }

  getTypeBadgeClass(type: any): string {
    if (!type) return 'bg-secondary';

    // Mapping par ID si disponible
    if (typeof type === 'object' && type.id) {
      const id = type.id;
      // 1: Paiements, 3: Annulation, 5: Contenu inapproprié, 6: Comportement abusif -> Danger
      if ([1, 3, 5, 6].includes(id)) return 'bg-danger';
      // 2: Retard, 8: Compte -> Warning
      if ([2, 8].includes(id)) return 'bg-warning';
      // 4: Qualité, 9: Données, 11: Qualité 2 -> Info
      if ([4, 9, 11].includes(id)) return 'bg-info';
      // 7: Support -> Dark
      if ([7].includes(id)) return 'bg-dark';
      // 10: Autre -> Secondary
      return 'bg-secondary';
    }

    // Fallback par nom ou code
    const typeKey = typeof type === 'string' ? type : type.name || '';
    const classes: { [key: string]: string } = {
      'Paiements': 'bg-danger',
      'Retard de livraison': 'bg-warning',
      'Annulation de commande': 'bg-danger',
      'Qualité du contenu': 'bg-info',
      'Qualité du contenu 2': 'bg-info',
      'Contenu inapproprié': 'bg-danger',
      'Comportement abusif': 'bg-danger',
      'Support technique': 'bg-dark',
      'Compte & accès': 'bg-warning',
      'Données personnelles': 'bg-info',
      'Autre': 'bg-secondary',

      // Anciens codes
      'payment_failed': 'bg-danger',
      'payment_pending': 'bg-warning',
      'service_quality': 'bg-info',
      'platform': 'bg-dark',
      'other': 'bg-secondary'
    };
    return classes[typeKey] || 'bg-secondary';
  }

  getStatusLabel(status: string): string {
    const statuses: { [key: string]: string } = {
      'pending': 'En attente',
      'accepted': 'Accepté',
      'rejected': 'Rejeté',
      'resolved': 'Résolu',
      'in_progress': 'En cours'
    };
    return statuses[status] || status;
  }

  getStatusBadgeClass(status: string): string {
    const classes: { [key: string]: string } = {
      'pending': 'bg-warning',
      'accepted': 'bg-success',
      'rejected': 'bg-danger',
      'resolved': 'bg-info',
      'in_progress': 'bg-primary'
    };
    return classes[status] || 'bg-secondary';
  }

  getStatusDisplay(status: string) {
    switch (status) {
      case "resolved":
      case "résolue":
        return { class: "status-accepted", text: "Résolue" };
      case "in_progress":
        return { class: "status-unknown", text: "En cours de traitement" };
      case "validated":
      case "validée":
        return { class: "status-accepted", text: "Validée" };
      case "rejected":
      case "rejetée":
        return { class: "status-rejected", text: "Rejetée" };
      case "pending":
      case "en_attente":
        return { class: "status-pending", text: "En attente" };
      case "in_analysis":
      case "en_cours_analyse":
        return { class: "status-unknown", text: "En cours d'analyse" };
      case "closed":
      case "fermée":
        return { class: "status-unknown", text: "Fermée" };
      default:
        return { class: "status-pending", text: "En attente" };
    }
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  openDetailModal(complaint: Complaint) {
    const modalRef = this.modalService.open(ComplaintDetailModalComponent, {
      size: 'lg',
      centered: true,
      scrollable: true
    });
    modalRef.componentInstance.complaint = complaint;
  }

  updateComplaintStatus(complaint: Complaint, newStatus: string, message: string = '') {
    // Mettre à jour le statut localement
    const index = this.complaints.findIndex(c => c.id === complaint.id);
    if (index !== -1) {
      // Sauvegarder l'ancien statut au cas où la requête échoue
      const oldStatus = this.complaints[index].status;
      this.complaints[index].status = newStatus;
      this.filterComplaintsByUserType();

      // Appeler l'API pour mettre à jour en base de données
      // Utilisation de POST avec _method pour contourner les restrictions CORS/Method sur PATCH
      const payload = {
        status: newStatus,
        message: message || this.getDefaultMessage(newStatus),
        _method: 'PATCH'
      };

      this.entityService.justSave(`/complaints/${complaint.id}/status`, payload).subscribe({
        next: (response: any) => {
          this.notificationService.success('Statut mis à jour avec succès');
        },
        error: (error) => {
          // Rétablir l'ancien statut en cas d'erreur
          this.complaints[index].status = oldStatus;
          this.filterComplaintsByUserType();
          this.notificationService.error('Erreur lors de la mise à jour du statut');
        }
      });
    }
  }

  getDefaultMessage(status: string): string {
    switch (status) {
      case 'in_progress':
        return 'Votre plainte est en cours de traitement';
      case 'resolved':
        return 'Votre plainte a été résolue';
      case 'rejected':
        return 'Votre plainte a été rejetée';
      default:
        return 'Statut mis à jour';
    }
  }

  // Méthodes pour les transitions de statut
  validateComplaint(complaint: Complaint) {
    this.updateComplaintStatus(complaint, 'in_progress', 'Votre plainte a été validée et est en cours de traitement');
  }

  rejectComplaint(complaint: Complaint) {
    this.updateComplaintStatus(complaint, 'rejected', 'Votre plainte a été rejetée');
  }

  startAnalysis(complaint: Complaint) {
    this.updateComplaintStatus(complaint, 'in_progress', 'L\'analyse de votre plainte a commencé');
  }

  resolveComplaint(complaint: Complaint) {
    this.updateComplaintStatus(complaint, 'resolved', 'Votre plainte a été résolue');
  }

  closeComplaint(complaint: Complaint) {
    this.updateComplaintStatus(complaint, 'resolved', 'Votre plainte a été fermée');
  }

  // Obtenir les actions possibles selon le statut actuel
  getAvailableActions(status: string): string[] {
    switch (status) {
      case 'pending':
      case 'en_attente':
        return ['validate', 'reject'];
      case 'in_progress':
      case 'en_cours_analyse':
      case 'validated': // Keeping for backward compatibility or if backend returns this
        return ['resolve', 'close'];
      case 'resolved':
      case 'résolue':
      case 'rejected':
      case 'rejetée':
      case 'closed':
      case 'fermée':
        return [];
      default:
        return ['validate', 'reject'];
    }
  }

  // Pagination methods
  changePage(page: number) {
    const currentPagination = this.getCurrentPagination();
    if (page >= 1 && page <= currentPagination.last_page) {
      currentPagination.current_page = page;
      this.updatePaginations();
    }
  }

  changePerPage(perPage: number) {
    const currentPagination = this.getCurrentPagination();
    currentPagination.per_page = perPage;
    currentPagination.current_page = 1;
    this.updatePaginations();
  }

  getPageNumbers(): number[] {
    const currentPagination = this.getCurrentPagination();
    const pages: number[] = [];
    const start = Math.max(1, currentPagination.current_page - 2);
    const end = Math.min(currentPagination.last_page, currentPagination.current_page + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  getPaginatedStarComplaints(): Complaint[] {
    const result = this.starComplaints.slice(
      (this.starPagination.current_page - 1) * this.starPagination.per_page,
      this.starPagination.current_page * this.starPagination.per_page
    );

    return result;
  }

  getPaginatedFanComplaints(): Complaint[] {
    const startIndex = (this.fanPagination.current_page - 1) * this.fanPagination.per_page;
    const endIndex = startIndex + this.fanPagination.per_page;
    return this.fanComplaints.slice(startIndex, endIndex);
  }

  getPaginatedPlatformComplaints(): Complaint[] {
    const startIndex = (this.platformPagination.current_page - 1) * this.platformPagination.per_page;
    const endIndex = startIndex + this.platformPagination.per_page;
    return this.platformComplaints.slice(startIndex, endIndex);
  }
  isValidImage(url: any): boolean {
    if (!url) return false;
    if (typeof url !== 'string') return false;
    const trimmed = url.trim().toLowerCase();
    if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') return false;
    const defaultPatterns = ['default.png', 'user.png', 'avatar.png', 'default-avatar'];
    if (defaultPatterns.some(pattern => trimmed.includes(pattern))) return false;
    return true;
  }

  onImageError(obj: any) {
    if (obj) {
      obj._hideImage = true;
    }
  }

  getDisplayName(user: any): string {
    if (!user) return 'Inconnu';
    return user.scene_name || (user.prenom + ' ' + user.nom).trim() || user.name || 'Inconnu';
  }

  getAvatarInitial(name: string): string {
    if (!name || name.startsWith('Utilisateur #')) return '?';
    return name.trim().charAt(0).toUpperCase();
  }

  getAvatarColor(name: string): string {
    if (!name || name === 'Yokastar' || name.startsWith('Utilisateur #')) return '#0389C9';
    const colors = [
      '#FF5722', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
      '#2196F3', '#00BCD4', '#009688', '#4CAF50', '#8BC34A',
      '#CDDC39', '#FFC107', '#FF9800'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }
}